/**
 * LifeGoalsPage — Family financial milestones tracker
 *
 * Shows goal cards with progress, target dates, monthly savings needed,
 * priority and status badges. Full CRUD via /api/life-goals.
 */
import { useEffect, useState, useCallback, lazy, Suspense } from 'react'
import { useSearchParams } from 'react-router-dom'
import {
  Target, Plus, X, Trash2, Edit3, GraduationCap, Home, Plane,
  Heart, Flame, Car, Hammer, HelpCircle,
} from 'lucide-react'
import { clsx } from 'clsx'
import {
  api,
  getLifeGoals,
  deleteLifeGoal,
} from '@/lib/api'
import type { LifeGoal } from '@/lib/api'

// ── backend contract (canonical, lowercase) ─────────────────────────────────
// POST/PUT /api/life-goals speak _gbp-suffixed amounts and lowercase
// priority/status Literals. Payloads conform here; responses are normalised
// back to the shape the page renders (target_amount / current_saved aliases).

interface LifeGoalPayload {
  name: string
  category: string
  target_amount_gbp: number
  current_saved_gbp: number
  target_date: string
  priority: 'high' | 'medium' | 'low'
  status?: 'active' | 'achieved' | 'paused' | 'cancelled'
  notes?: string
}

/** Alias the *_gbp response fields like getLifeGoals does, so summary
 *  reductions never see undefined and render £NaN. */
function normaliseGoal(g: LifeGoal): LifeGoal {
  return {
    ...g,
    target_amount: g.target_amount ?? g.target_amount_gbp ?? 0,
    current_saved: g.current_saved ?? g.current_saved_gbp ?? 0,
    monthly_savings_needed: g.monthly_savings_needed ?? g.monthly_savings_needed_gbp ?? null,
  }
}

// ─── helpers ────────────────────────────────────────────────────────────────

function fmt(n: number | null | undefined) {
  if (n == null) return '£0'
  return `£${Number(n).toLocaleString('en-GB', { maximumFractionDigits: 0 })}`
}

function monthsAway(dateStr: string): string {
  const target = new Date(dateStr)
  const now = new Date()
  const diff = (target.getFullYear() - now.getFullYear()) * 12 + (target.getMonth() - now.getMonth())
  if (diff <= 0) return 'Overdue'
  if (diff === 1) return '1 month away'
  if (diff < 12) return `${diff} months away`
  const years = Math.floor(diff / 12)
  const months = diff % 12
  if (months === 0) return `${years} year${years === 1 ? '' : 's'} away`
  return `${years}y ${months}m away`
}

function progressColor(pct: number): string {
  if (pct >= 75) return '#22c55e'
  if (pct >= 50) return '#10b981'
  if (pct >= 25) return '#f59e0b'
  return '#ef4444'
}

function progressBarClass(pct: number): string {
  if (pct >= 75) return 'bg-green-500'
  if (pct >= 50) return 'bg-emerald-500'
  if (pct >= 25) return 'bg-amber-500'
  return 'bg-red-500'
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const CATEGORY_ICONS: Record<string, React.ComponentType<any>> = {
  education:   GraduationCap,
  property:    Home,
  travel:      Plane,
  wedding:     Heart,
  retirement:  Flame,
  car:         Car,
  renovation:  Hammer,
  other:       HelpCircle,
}

const CATEGORY_LABELS: Record<string, string> = {
  education:  'Education',
  property:   'Property',
  travel:     'Travel',
  wedding:    'Wedding',
  retirement: 'Retirement',
  car:        'Car',
  renovation: 'Renovation',
  other:      'Other',
}

const CATEGORY_COLORS: Record<string, string> = {
  education:  'text-blue-400 bg-blue-500/10',
  property:   'text-emerald-400 bg-emerald-500/10',
  travel:     'text-cyan-400 bg-cyan-500/10',
  wedding:    'text-pink-400 bg-pink-500/10',
  retirement: 'text-orange-400 bg-orange-500/10',
  car:        'text-purple-400 bg-purple-500/10',
  renovation: 'text-amber-400 bg-amber-500/10',
  other:      'text-text-muted bg-surface-hover',
}

// Keyed on the backend's lowercase Literals; look up via .toLowerCase().
const PRIORITY_BADGE: Record<string, string> = {
  high:   'badge badge-red',
  medium: 'badge badge-amber',
  low:    'badge-outline text-text-muted text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded border border-border',
}

const STATUS_BADGE: Record<string, string> = {
  active:   'badge badge-green',
  achieved: 'badge bg-emerald-500/15 text-emerald-400 border border-emerald-500/30',
  paused:   'badge bg-surface-hover text-text-muted border border-border',
}

// ─── Goal Card ───────────────────────────────────────────────────────────────

function GoalCard({
  goal,
  onEdit,
  onDelete,
}: {
  goal: LifeGoal
  onEdit: () => void
  onDelete: () => void
}) {
  const targetAmt = goal.target_amount ?? goal.target_amount_gbp ?? 0
  const currentSaved = goal.current_saved ?? goal.current_saved_gbp ?? 0
  const pct = targetAmt > 0
    ? Math.min((currentSaved / targetAmt) * 100, 100)
    : 0

  const Icon = CATEGORY_ICONS[goal.category] ?? HelpCircle
  const colorClass = CATEGORY_COLORS[goal.category] ?? CATEGORY_COLORS.other

  return (
    <article
      className="card-hover p-5 flex flex-col gap-4"
      aria-label={`${goal.name} life goal`}
    >
      {/* Header row */}
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <div className={clsx('w-9 h-9 rounded-xl flex items-center justify-center shrink-0', colorClass)}>
            <Icon size={18} aria-hidden />
          </div>
          <div className="min-w-0">
            <p className="font-semibold text-text-primary leading-tight truncate">{goal.name}</p>
            <p className="text-xs text-text-muted mt-0.5">{CATEGORY_LABELS[goal.category] ?? 'Other'}</p>
          </div>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <button
            type="button"
            onClick={onEdit}
            className="p-1.5 rounded hover:bg-surface-hover transition-colors"
            aria-label="Edit goal"
          >
            <Edit3 size={14} className="text-text-muted" />
          </button>
          <button
            type="button"
            onClick={onDelete}
            className="p-1.5 rounded hover:bg-red-500/10 transition-colors"
            aria-label="Delete goal"
          >
            <Trash2 size={14} className="text-red-400" />
          </button>
        </div>
      </div>

      {/* Badges — backend stores lowercase; compare case-insensitively */}
      <div className="flex items-center gap-2 flex-wrap">
        <span className={STATUS_BADGE[(goal.status ?? '').toLowerCase()] ?? STATUS_BADGE.active}>
          {(goal.status ?? 'active').toUpperCase()}
        </span>
        <span className={PRIORITY_BADGE[(goal.priority ?? '').toLowerCase()] ?? PRIORITY_BADGE.low}>
          {(goal.priority ?? 'medium').toUpperCase()}
        </span>
      </div>

      {/* Progress */}
      <div>
        <div className="flex justify-between text-xs text-text-muted mb-1.5">
          <span>{fmt(currentSaved)} saved</span>
          <span>{fmt(targetAmt)} target</span>
        </div>
        <div
          role="progressbar"
          aria-valuenow={Math.round(pct)}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-label={`${pct.toFixed(0)}% of goal saved`}
          className="h-2.5 bg-surface-hover rounded-full overflow-hidden"
        >
          <div
            className={clsx('h-full rounded-full transition-all duration-700', progressBarClass(pct))}
            style={{ width: `${pct}%` }}
          />
        </div>
        <p
          className="text-right text-xs mt-1 font-medium tabular-nums"
          style={{ color: progressColor(pct) }}
        >
          {pct.toFixed(1)}%
        </p>
      </div>

      {/* Stats grid */}
      <div className="grid grid-cols-2 gap-2">
        <div className="bg-surface-DEFAULT rounded-lg p-3">
          <p className="text-[10px] text-text-muted uppercase tracking-wider">Target date</p>
          <p className="text-sm font-semibold text-text-primary mt-0.5">
            {goal.target_date
              ? new Date(goal.target_date).toLocaleDateString('en-GB', { month: 'short', year: 'numeric' })
              : '—'}
          </p>
          <p className="text-[10px] text-text-muted mt-0.5">
            {goal.target_date ? monthsAway(goal.target_date) : 'No target date'}
          </p>
        </div>
        <div className="bg-surface-DEFAULT rounded-lg p-3">
          <p className="text-[10px] text-text-muted uppercase tracking-wider">Monthly needed</p>
          <p className="text-sm font-semibold text-text-primary mt-0.5">
            {goal.monthly_savings_needed != null ? fmt(goal.monthly_savings_needed) : '—'}
          </p>
          <p className="text-[10px] text-text-muted mt-0.5">to reach target</p>
        </div>
      </div>

      {goal.notes && (
        <p className="text-xs text-text-muted border-t border-border pt-3 leading-relaxed">
          {goal.notes}
        </p>
      )}
    </article>
  )
}

// ─── Goal Form Modal ─────────────────────────────────────────────────────────

type GoalFormProps = {
  initial?: Partial<LifeGoal>
  onClose: () => void
  onSubmit: (data: LifeGoalPayload) => Promise<void>
}

const CATEGORIES = Object.keys(CATEGORY_LABELS) as string[]
// Backend GoalPriority / GoalStatus Literals — lowercase, canonical.
const PRIORITIES = ['high', 'medium', 'low'] as const
const STATUSES   = ['active', 'achieved', 'paused'] as const

type PriorityValue = typeof PRIORITIES[number]
type StatusValue = 'active' | 'achieved' | 'paused' | 'cancelled'

function asPriority(v: string | undefined): PriorityValue {
  const low = (v ?? '').toLowerCase()
  return (PRIORITIES as readonly string[]).includes(low) ? (low as PriorityValue) : 'medium'
}

function asStatus(v: string | undefined): StatusValue {
  const low = (v ?? '').toLowerCase()
  return ['active', 'achieved', 'paused', 'cancelled'].includes(low) ? (low as StatusValue) : 'active'
}

function GoalFormModal({ initial, onClose, onSubmit }: GoalFormProps) {
  const [name,       setName]       = useState(initial?.name ?? '')
  const [category,   setCategory]   = useState(initial?.category ?? 'other')
  const [target,     setTarget]     = useState(String(initial?.target_amount ?? initial?.target_amount_gbp ?? ''))
  const [saved,      setSaved]      = useState(String(initial?.current_saved ?? initial?.current_saved_gbp ?? '0'))
  const [date,       setDate]       = useState((initial?.target_date ?? '').slice(0, 10))
  const [priority,   setPriority]   = useState<PriorityValue>(asPriority(initial?.priority))
  const [status,     setStatus]     = useState<StatusValue>(asStatus(initial?.status))
  const [notes,      setNotes]      = useState(initial?.notes ?? '')
  const [submitting, setSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!name || !target || !date) return
    setSubmitting(true)
    setSubmitError(null)
    try {
      await onSubmit({
        name,
        category,
        target_amount_gbp: parseFloat(target),
        current_saved_gbp: parseFloat(saved) || 0,
        target_date:       date,
        priority,
        status,
        notes: notes || undefined,
      })
      // Close only on success — a failed save keeps the modal (and the
      // user's input) alive with an inline error.
      onClose()
    } catch (err: any) {
      const detail = err?.response?.data?.detail
      setSubmitError(
        typeof detail === 'string'
          ? detail
          : (err?.message ?? 'Failed to save goal'),
      )
    } finally {
      setSubmitting(false)
    }
  }

  const isEdit = !!initial?.id

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
      <form
        onSubmit={handleSubmit}
        className="card p-6 w-full max-w-lg space-y-4 max-h-[90vh] overflow-y-auto"
      >
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-bold text-text-primary">
            {isEdit ? 'Edit Goal' : 'Add Life Goal'}
          </h2>
          <button type="button" onClick={onClose} className="p-1 hover:bg-surface-hover rounded" aria-label="Close">
            <X size={18} className="text-text-muted" />
          </button>
        </div>

        {/* Name */}
        <div>
          <label htmlFor="lg-name" className="block text-sm text-text-secondary mb-1">Goal Name</label>
          <input
            id="lg-name"
            value={name}
            onChange={e => setName(e.target.value)}
            placeholder="e.g. School fees for Ella"
            className="input w-full"
            required
          />
        </div>

        {/* Category */}
        <div>
          <label htmlFor="lg-category" className="block text-sm text-text-secondary mb-1">Category</label>
          <select
            id="lg-category"
            value={category}
            onChange={e => setCategory(e.target.value)}
            className="input w-full"
          >
            {CATEGORIES.map(c => (
              <option key={c} value={c}>{CATEGORY_LABELS[c]}</option>
            ))}
          </select>
        </div>

        {/* Amounts row */}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label htmlFor="lg-target" className="block text-sm text-text-secondary mb-1">Target (£)</label>
            <input
              id="lg-target"
              type="number"
              value={target}
              onChange={e => setTarget(e.target.value)}
              placeholder="50000"
              className="input w-full"
              min="1"
              step="100"
              required
            />
          </div>
          <div>
            <label htmlFor="lg-saved" className="block text-sm text-text-secondary mb-1">Already Saved (£)</label>
            <input
              id="lg-saved"
              type="number"
              value={saved}
              onChange={e => setSaved(e.target.value)}
              placeholder="0"
              className="input w-full"
              min="0"
              step="100"
            />
          </div>
        </div>

        {/* Date — monthly savings needed is computed by the backend from
            remaining amount ÷ months to target, so there is no input for it */}
        <div>
          <label htmlFor="lg-date" className="block text-sm text-text-secondary mb-1">Target Date</label>
          <input
            id="lg-date"
            type="date"
            value={date}
            onChange={e => setDate(e.target.value)}
            className="input w-full"
            required
          />
        </div>

        {/* Priority & status */}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label htmlFor="lg-priority" className="block text-sm text-text-secondary mb-1">Priority</label>
            <select
              id="lg-priority"
              value={priority}
              onChange={e => setPriority(e.target.value as typeof priority)}
              className="input w-full"
            >
              {PRIORITIES.map(p => <option key={p} value={p}>{p.toUpperCase()}</option>)}
            </select>
          </div>
          <div>
            <label htmlFor="lg-status" className="block text-sm text-text-secondary mb-1">Status</label>
            <select
              id="lg-status"
              value={status}
              onChange={e => setStatus(e.target.value as typeof status)}
              className="input w-full"
            >
              {STATUSES.map(s => <option key={s} value={s}>{s.toUpperCase()}</option>)}
            </select>
          </div>
        </div>

        {/* Notes */}
        <div>
          <label htmlFor="lg-notes" className="block text-sm text-text-secondary mb-1">Notes (optional)</label>
          <textarea
            id="lg-notes"
            value={notes}
            onChange={e => setNotes(e.target.value)}
            placeholder="Any context about this goal..."
            className="input w-full h-20 resize-none"
          />
        </div>

        {submitError && (
          <div className="rounded-lg border border-red-500/30 bg-red-500/5 px-3 py-2">
            <p className="text-xs text-red-400">{submitError}</p>
          </div>
        )}

        <div className="flex gap-3 pt-1">
          <button type="button" onClick={onClose} className="btn-secondary flex-1">Cancel</button>
          <button type="submit" className="btn-primary flex-1" disabled={submitting}>
            {submitting ? 'Saving…' : isEdit ? 'Save Changes' : 'Create Goal'}
          </button>
        </div>
      </form>
    </div>
  )
}

// ─── Main Page ───────────────────────────────────────────────────────────────

// Lazy tab content re-homed from the former standalone /app/goals page
// (Monte Carlo goal projections against the portfolio — different backend
// to life-goals, so it lives on intact as a tab rather than being rewritten).
const ProjectionsTab = lazy(() => import('./GoalsPage'))

type LifeGoalsTab = 'milestones' | 'projections'

const LIFE_GOALS_TABS: { key: LifeGoalsTab; label: string }[] = [
  { key: 'milestones',  label: 'Milestones' },
  { key: 'projections', label: 'Projections (Monte Carlo)' },
]

export default function LifeGoalsPage() {
  const [searchParams] = useSearchParams()
  const [tab, setTab] = useState<LifeGoalsTab>(() => {
    const t = searchParams.get('tab')
    return LIFE_GOALS_TABS.some(x => x.key === t) ? (t as LifeGoalsTab) : 'milestones'
  })
  const [goals,     setGoals]     = useState<LifeGoal[]>([])
  const [loading,   setLoading]   = useState(true)
  const [error,     setError]     = useState<string | null>(null)
  const [showAdd,   setShowAdd]   = useState(false)
  const [editing,   setEditing]   = useState<LifeGoal | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const data = await getLifeGoals()
      setGoals(data)
    } catch {
      setError('Failed to load goals. Make sure the backend is running.')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { load() }, [load])

  const handleCreate = async (body: LifeGoalPayload) => {
    // Backend forces status='active' on create, so don't send it.
    const { status: _status, ...createBody } = body
    const { data } = await api.post<LifeGoal>('/api/life-goals', createBody)
    setGoals(prev => [...prev, normaliseGoal(data)])
  }

  const handleUpdate = async (body: LifeGoalPayload) => {
    if (!editing) return
    const { data } = await api.put<LifeGoal>(`/api/life-goals/${editing.id}`, body)
    setGoals(prev => prev.map(g => g.id === editing.id ? normaliseGoal(data) : g))
    setEditing(null)
  }

  const handleDelete = async (id: number, name: string) => {
    if (!confirm(`Remove "${name}" from your goals?`)) return
    await deleteLifeGoal(id)
    setGoals(prev => prev.filter(g => g.id !== id))
  }

  // ── Summary stats — amounts normalised on entry; status stored lowercase ──
  const totalTarget   = goals.reduce((s, g) => s + (g.target_amount ?? g.target_amount_gbp ?? 0), 0)
  const totalSaved    = goals.reduce((s, g) => s + (g.current_saved ?? g.current_saved_gbp ?? 0), 0)
  const overallPct    = totalTarget > 0 ? (totalSaved / totalTarget) * 100 : 0
  const activeGoals   = goals.filter(g => (g.status ?? '').toUpperCase() === 'ACTIVE').length
  const achievedGoals = goals.filter(g => (g.status ?? '').toUpperCase() === 'ACHIEVED').length

  return (
    <div className="space-y-6 animate-fade-in">
      {/* ── Header ── */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="page-title">Life Goals</h1>
          <p className="text-sm text-text-secondary">
            Track your family's financial milestones
          </p>
        </div>
        <button
          type="button"
          onClick={() => setShowAdd(true)}
          className="btn-primary flex items-center gap-2 text-sm"
        >
          <Plus size={16} aria-hidden />
          Add Goal
        </button>
      </div>

      {/* ── Tabs ── */}
      <div className="flex items-center gap-1 border-b border-border flex-wrap">
        {LIFE_GOALS_TABS.map(t => (
          <button
            key={t.key}
            type="button"
            onClick={() => setTab(t.key)}
            className={
              tab === t.key
                ? 'px-4 py-2.5 text-sm font-medium border-b-2 border-blue-light text-blue-light -mb-px'
                : 'px-4 py-2.5 text-sm font-medium text-text-muted hover:text-text-secondary border-b-2 border-transparent -mb-px'
            }
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* ── Projections tab (ex-/app/goals Monte Carlo page) ── */}
      {tab === 'projections' && (
        <Suspense fallback={<div className="p-8 text-text-muted">Loading projections...</div>}>
          <ProjectionsTab />
        </Suspense>
      )}

      {tab === 'milestones' && (<>
      {/* ── Summary stat cards ── */}
      {goals.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 stagger-children">
          <div className="card p-4">
            <p className="stat-label">Total Goals Value</p>
            <p className="text-lg font-bold text-text-primary mt-1">{fmt(totalTarget)}</p>
          </div>
          <div className="card p-4">
            <p className="stat-label">Total Saved</p>
            <p className="text-lg font-bold text-emerald-400 mt-1">{fmt(totalSaved)}</p>
          </div>
          <div className="card p-4">
            <p className="stat-label">Overall Progress</p>
            <p
              className="stat-hero mt-1 tabular-nums"
              style={{ color: progressColor(overallPct) }}
            >
              {overallPct.toFixed(1)}%
            </p>
          </div>
          <div className="card p-4">
            <p className="stat-label">Goals</p>
            <p className="text-lg font-bold text-text-primary mt-1">
              {activeGoals} active
              {achievedGoals > 0 && (
                <span className="text-sm text-emerald-400 ml-1.5">· {achievedGoals} achieved</span>
              )}
            </p>
          </div>
        </div>
      )}

      {/* ── Error ── */}
      {error && (
        <div className="card p-4 border border-red-500/30 bg-red-500/5">
          <p className="text-sm text-red-400">{error}</p>
          <button type="button" onClick={load} className="btn-secondary text-xs mt-2">
            Retry
          </button>
        </div>
      )}

      {/* ── Loading skeleton ── */}
      {loading && (
        <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-5">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="card p-5 space-y-4">
              <div className="flex gap-3">
                <div className="w-9 h-9 rounded-xl skeleton" />
                <div className="space-y-1.5 flex-1">
                  <div className="h-3.5 skeleton w-3/4" />
                  <div className="h-2.5 skeleton w-1/3" />
                </div>
              </div>
              <div className="h-2.5 skeleton rounded-full" />
              <div className="grid grid-cols-2 gap-2">
                <div className="h-14 skeleton rounded-lg" />
                <div className="h-14 skeleton rounded-lg" />
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ── Empty state ── */}
      {!loading && goals.length === 0 && !error && (
        <div className="card p-12 text-center space-y-4">
          <div className="w-14 h-14 rounded-2xl bg-blue-500/10 flex items-center justify-center mx-auto">
            <Target size={28} className="text-blue-400" aria-hidden />
          </div>
          <div>
            <p className="text-text-primary font-semibold">No goals yet</p>
            <p className="text-sm text-text-muted mt-1">
              Add your first family financial milestone — school fees, a property deposit, a dream holiday.
            </p>
          </div>
          <button
            type="button"
            onClick={() => setShowAdd(true)}
            className="btn-primary inline-flex items-center gap-2 text-sm"
          >
            <Plus size={16} aria-hidden />
            Add your first goal
          </button>
        </div>
      )}

      {/* ── Goal cards grid ── */}
      {!loading && goals.length > 0 && (
        <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-5 stagger-children">
          {goals.map(goal => (
            <GoalCard
              key={goal.id}
              goal={goal}
              onEdit={() => setEditing(goal)}
              onDelete={() => handleDelete(goal.id, goal.name)}
            />
          ))}
        </div>
      )}

      {/* ── Summary footer ── */}
      {goals.length > 0 && (
        <div className="card p-4">
          <div className="flex items-center gap-3 mb-3">
            <Target size={15} className="text-text-muted" aria-hidden />
            <p className="panel-title">Overall Progress</p>
          </div>
          <div className="flex justify-between text-xs text-text-muted mb-1.5">
            <span>{fmt(totalSaved)} saved across {goals.length} goal{goals.length !== 1 ? 's' : ''}</span>
            <span>{fmt(totalTarget)} total target</span>
          </div>
          <div
            role="progressbar"
            aria-valuenow={Math.round(overallPct)}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-label={`Overall ${overallPct.toFixed(0)}% saved`}
            className="h-3 bg-surface-hover rounded-full overflow-hidden"
          >
            <div
              className={clsx('h-full rounded-full transition-all duration-700', progressBarClass(overallPct))}
              style={{ width: `${overallPct}%` }}
            />
          </div>
        </div>
      )}

      {/* ── Modals ── */}
      {showAdd && (
        <GoalFormModal
          onClose={() => setShowAdd(false)}
          onSubmit={handleCreate}
        />
      )}
      {editing && (
        <GoalFormModal
          initial={editing}
          onClose={() => setEditing(null)}
          onSubmit={handleUpdate}
        />
      )}
      </>)}
    </div>
  )
}
